/* Canonical notification event registry shared by frontend and backend. */

const NOTIFICATION_GROUPS = Object.freeze({
  DIRECT: "direct_actions",
  ACTIVITY: "activity_updates",
  SCHEDULE: "deadlines_schedule",
  INACTIVITY: "overdue_inactivity",
  CALENDAR_LEAVE: "calendar_leave",
  OPERATIONS: "operations_team",
  DRUGS: "drugs_inventory",
});

const NOTIFICATION_CLASSIFICATIONS = Object.freeze({
  PERSONAL: "personal",
  TEAM: "team",
  APPROVAL: "approval",
  SYSTEM: "system",
});

const NOTIFICATION_TARGET_TYPES = Object.freeze({
  DETAIL_ROUTE: "detail_route",
  BOARD_ROUTE: "board_route",
  MODAL_SURFACE: "modal_surface",
  INFORMATIONAL_ONLY: "informational_only",
});

const NOTIFICATION_EMAIL_CTA_TYPES = Object.freeze({
  NONE: "none",
  STATIC: "static",
  VIEW_CONTEXT_TYPE: "view_context_type",
  VIEW_MAINTENANCE_STREAM: "view_maintenance_stream",
});

const RECIPIENT_STRATEGIES = Object.freeze({
  DIRECT_USER: "direct_user",
  TEAM_TARGETS: "team_targets",
  ROLE_TARGETS: "role_targets",
  APPROVAL_CHAIN: "approval_chain",
  SYSTEM_ACTOR: "system_actor",
});

const NOTIFICATION_SETTINGS_SURFACES = Object.freeze({
  STANDARD: "standard",
  TEAM: "team",
});

const RETIRED_NOTIFICATION_EVENT_IDS = Object.freeze([
  "issue_manager",
  "service_manager",
  "new_asset_issue",
  "team_asset_issue_new",
  "team_asset_issue_update",
  "team_asset_service",
  "team_asset_service_overdue",
]);

const USER_CONTROLLED_CHANNELS = Object.freeze({
  inApp: true,
  email: true,
});

const NOTIFICATION_ROUTE_TARGETS = Object.freeze({
  drug_actions: Object.freeze({
    type: NOTIFICATION_TARGET_TYPES.BOARD_ROUTE,
    key: "drug_actions",
    view: "drugs",
    subView: "actions",
    path: "/drugs/actions",
  }),
  drug_stock: Object.freeze({
    type: NOTIFICATION_TARGET_TYPES.BOARD_ROUTE,
    key: "drug_stock",
    view: "drugs",
    subView: "stock",
    path: "/drugs/stock",
  }),
  drug_history: Object.freeze({
    type: NOTIFICATION_TARGET_TYPES.BOARD_ROUTE,
    key: "drug_history",
    view: "drugs",
    subView: "history",
    path: "/drugs/history",
  }),
  drug_audit: Object.freeze({
    type: NOTIFICATION_TARGET_TYPES.BOARD_ROUTE,
    key: "drug_audit",
    view: "drugs",
    subView: "audit",
    path: "/drugs/audit",
  }),
  labs_board: Object.freeze({
    type: NOTIFICATION_TARGET_TYPES.BOARD_ROUTE,
    key: "labs_board",
    view: "labs",
    path: "/labs",
  }),
  maintenance_board: Object.freeze({
    type: NOTIFICATION_TARGET_TYPES.BOARD_ROUTE,
    key: "maintenance_board",
    view: "maintenance",
    path: "/maintenance",
  }),
  recalls_board: Object.freeze({
    type: NOTIFICATION_TARGET_TYPES.BOARD_ROUTE,
    key: "recalls_board",
    view: "recalls",
    path: "/recalls",
  }),
  calendar_board: Object.freeze({
    type: NOTIFICATION_TARGET_TYPES.BOARD_ROUTE,
    key: "calendar_board",
    view: "calendar",
    path: "/calendar",
  }),
  ordering_board: Object.freeze({
    type: NOTIFICATION_TARGET_TYPES.BOARD_ROUTE,
    key: "ordering_board",
    view: "ordering",
    path: "/ordering",
    surface: "ordering_item_modal",
    contextType: "ordering_item",
  }),
  compliance_board: Object.freeze({
    type: NOTIFICATION_TARGET_TYPES.BOARD_ROUTE,
    key: "compliance_board",
    view: "compliance",
    path: "/compliance",
    surface: "compliance_record_modal",
    contextType: "compliance_requirement",
  }),
});

const GROUP_DEFINITIONS = Object.freeze([
  {
    id: NOTIFICATION_GROUPS.DIRECT,
    label: "Direct actions & assignments",
    order: 10,
  },
  {
    id: NOTIFICATION_GROUPS.ACTIVITY,
    label: "Activity & updates",
    order: 20,
  },
  {
    id: NOTIFICATION_GROUPS.SCHEDULE,
    label: "Deadlines & schedule",
    order: 30,
  },
  {
    id: NOTIFICATION_GROUPS.INACTIVITY,
    label: "Overdue & inactivity",
    order: 40,
  },
  {
    id: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    label: "Calendar & leave",
    order: 50,
  },
  {
    id: NOTIFICATION_GROUPS.OPERATIONS,
    label: "Operations & teams",
    order: 60,
  },
  {
    id: NOTIFICATION_GROUPS.DRUGS,
    label: "Drugs & Inventory",
    order: 70,
  },
]);

function event({
  id,
  label,
  groupId,
  order,
  classification = NOTIFICATION_CLASSIFICATIONS.PERSONAL,
  recipientStrategy = RECIPIENT_STRATEGIES.DIRECT_USER,
  targetType = NOTIFICATION_TARGET_TYPES.DETAIL_ROUTE,
  channels = USER_CONTROLLED_CHANNELS,
  rendererKey = id,
  emailRendererKey = id,
  targetKey = id,
  settingsSurface = null,
  visibilityPermissions = [],
  visibilityRoles = [],
  defaultControl = null,
  defaultSetting = null,
  defaultTeamSetting = null,
  cleanupPolicy = null,
  emailAction = null,
}) {
  return Object.freeze({
    id,
    label,
    groupId,
    order,
    classification,
    recipientStrategy,
    target: Object.freeze({
      type: targetType,
      key: targetKey,
    }),
    channels: Object.freeze({...channels}),
    rendererKey,
    emailRendererKey,
    settingsSurface: settingsSurface ||
      (classification === NOTIFICATION_CLASSIFICATIONS.TEAM ?
        NOTIFICATION_SETTINGS_SURFACES.TEAM :
        NOTIFICATION_SETTINGS_SURFACES.STANDARD),
    visibilityPermissions: Object.freeze([...visibilityPermissions]),
    visibilityRoles: Object.freeze([...visibilityRoles]),
    defaultControl: defaultControl ? Object.freeze({...defaultControl}) : null,
    defaultSetting: defaultSetting ? Object.freeze({...defaultSetting}) : null,
    defaultTeamSetting: defaultTeamSetting ?
      Object.freeze({...defaultTeamSetting}) :
      null,
    emailAction: emailAction ? Object.freeze({
      cta: emailAction.cta ? Object.freeze({...emailAction.cta}) : null,
      target: emailAction.target || null,
      targetKey: emailAction.targetKey || null,
    }) : null,
    cleanupPolicy: cleanupPolicy ? Object.freeze({
      identityField: cleanupPolicy.identityField,
      supersedesEvents: Object.freeze([
        ...(cleanupPolicy.supersedesEvents || []),
      ]),
      clearOlderSameEvent: cleanupPolicy.clearOlderSameEvent === true,
    }) : null,
  });
}

const directControl = Object.freeze({
  visibility: "visible",
  inApp: "user_control",
  email: "user_control",
});

const inAppDefault = Object.freeze({
  inApp: true,
  email: false,
});
const timedControl = Object.freeze({
  visibility: "visible",
  inApp: "user_control",
  email: "user_control",
  days: "user_control",
});
const reminderDefault = Object.freeze({
  inApp: true,
  email: false,
  days: 7,
});
const inactivityDefault = Object.freeze({
  inApp: true,
  email: false,
  days: 14,
});
const teamInAppDefault = Object.freeze({
  teamId: "",
  inApp: true,
  email: false,
});
const teamTimedDefault = Object.freeze({
  ...teamInAppDefault,
  daysInAdvance: 14,
});
const teamPriorityDefault = Object.freeze({
  ...teamInAppDefault,
  targets: [],
});
const teamTimedPriorityDefault = Object.freeze({
  ...teamTimedDefault,
  targets: [],
});
const teamCountDefault = Object.freeze({
  ...teamInAppDefault,
  countThreshold: 10,
});

const detail = NOTIFICATION_TARGET_TYPES.DETAIL_ROUTE;
const board = NOTIFICATION_TARGET_TYPES.BOARD_ROUTE;
const modal = NOTIFICATION_TARGET_TYPES.MODAL_SURFACE;
const info = NOTIFICATION_TARGET_TYPES.INFORMATIONAL_ONLY;
const direct = RECIPIENT_STRATEGIES.DIRECT_USER;
const team = RECIPIENT_STRATEGIES.TEAM_TARGETS;
const role = RECIPIENT_STRATEGIES.ROLE_TARGETS;
const approval = RECIPIENT_STRATEGIES.APPROVAL_CHAIN;
const system = RECIPIENT_STRATEGIES.SYSTEM_ACTOR;
const noEmailCta = Object.freeze({
  cta: Object.freeze({type: NOTIFICATION_EMAIL_CTA_TYPES.NONE}),
});
const viewContextEmailAction = Object.freeze({
  cta: Object.freeze({
    type: NOTIFICATION_EMAIL_CTA_TYPES.VIEW_CONTEXT_TYPE,
  }),
  target: "same",
});
const viewMaintenanceStreamEmailAction = Object.freeze({
  cta: Object.freeze({
    type: NOTIFICATION_EMAIL_CTA_TYPES.VIEW_MAINTENANCE_STREAM,
  }),
  target: "same",
});
const reviewRequestEmailAction = Object.freeze({
  cta: Object.freeze({
    type: NOTIFICATION_EMAIL_CTA_TYPES.STATIC,
    label: "Review Request",
  }),
  targetKey: "drug_history",
});
const resubmitTransactionEmailAction = Object.freeze({
  cta: Object.freeze({
    type: NOTIFICATION_EMAIL_CTA_TYPES.STATIC,
    label: "Resubmit Transaction",
  }),
  targetKey: "drug_actions",
});
const reviewStockEmailAction = Object.freeze({
  cta: Object.freeze({
    type: NOTIFICATION_EMAIL_CTA_TYPES.STATIC,
    label: "Review Stock",
  }),
  targetKey: "drug_stock",
});
const reviewAuditEmailAction = Object.freeze({
  cta: Object.freeze({
    type: NOTIFICATION_EMAIL_CTA_TYPES.STATIC,
    label: "Review Audit",
  }),
  targetKey: "drug_history",
});
const auditDrugsEmailAction = Object.freeze({
  cta: Object.freeze({
    type: NOTIFICATION_EMAIL_CTA_TYPES.STATIC,
    label: "Audit Drugs",
  }),
  targetKey: "drug_audit",
});
const reviewRecallsEmailAction = Object.freeze({
  cta: Object.freeze({
    type: NOTIFICATION_EMAIL_CTA_TYPES.STATIC,
    label: "Review Recalls",
  }),
  targetKey: "recalls_board",
});
const openCalendarEmailAction = Object.freeze({
  cta: Object.freeze({
    type: NOTIFICATION_EMAIL_CTA_TYPES.STATIC,
    label: "Open Calendar",
  }),
  targetKey: "calendar_board",
});
const openOrderingEmailAction = Object.freeze({
  cta: Object.freeze({
    type: NOTIFICATION_EMAIL_CTA_TYPES.STATIC,
    label: "Open Ordering",
  }),
  targetKey: "ordering_board",
});
const reviewCalendarRequestEmailAction = Object.freeze({
  cta: Object.freeze({
    type: NOTIFICATION_EMAIL_CTA_TYPES.STATIC,
    label: "Review Request",
  }),
  targetKey: "calendar_board",
});

const EVENT_DEFINITIONS = Object.freeze([
  event({
    id: "tag_note",
    label: "Tagged in a note",
    groupId: NOTIFICATION_GROUPS.DIRECT,
    order: 10,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "tag_comment",
    label: "Tagged in item details",
    groupId: NOTIFICATION_GROUPS.DIRECT,
    order: 20,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "task_assign",
    label: "Task assigned",
    groupId: NOTIFICATION_GROUPS.DIRECT,
    order: 30,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "task_owner",
    label: "Task owner",
    groupId: NOTIFICATION_GROUPS.DIRECT,
    order: 40,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "parent_manager",
    label: "Item manager",
    groupId: NOTIFICATION_GROUPS.DIRECT,
    order: 50,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "maintenance_manager",
    label: "Maintenance manager",
    groupId: NOTIFICATION_GROUPS.DIRECT,
    order: 55,
    visibilityPermissions: ["maintenance"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: viewMaintenanceStreamEmailAction,
  }),
  event({
    id: "patient_provider_added",
    label: "Patient provider",
    groupId: NOTIFICATION_GROUPS.DIRECT,
    order: 60,
    visibilityPermissions: ["patients"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "labs_provider_added",
    label: "Lab provider",
    groupId: NOTIFICATION_GROUPS.DIRECT,
    order: 65,
    visibilityPermissions: ["labs"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "labs_assigned_user_added",
    label: "Lab assigned user",
    groupId: NOTIFICATION_GROUPS.DIRECT,
    order: 70,
    visibilityPermissions: ["labs"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "labs_patient_case_manager_new_lab",
    label: "Linked patient lab item",
    groupId: NOTIFICATION_GROUPS.DIRECT,
    order: 75,
    visibilityPermissions: ["labs"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "user_invite_claimed",
    label: "User invite claimed",
    groupId: NOTIFICATION_GROUPS.DIRECT,
    order: 90,
    classification: NOTIFICATION_CLASSIFICATIONS.SYSTEM,
    targetType: info,
    recipientStrategy: system,
    visibilityRoles: ["admin"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: noEmailCta,
  }),
  event({
    id: "event_created",
    label: "Event created",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 10,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "event_updated",
    label: "Event updated",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 20,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [
        "event_created",
        "event_upcoming",
        "event_today",
        "event_due",
      ],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "event_deleted",
    label: "Event deleted",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 30,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [
        "event_created",
        "event_updated",
        "event_upcoming",
        "event_today",
        "event_due",
      ],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "leave_approval_required",
    label: "Leave approval required",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 40,
    classification: NOTIFICATION_CLASSIFICATIONS.APPROVAL,
    recipientStrategy: approval,
    targetType: modal,
    targetKey: "approval",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: reviewCalendarRequestEmailAction,
  }),
  event({
    id: "reminder_deleted",
    label: "Reminder deleted",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 50,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [
        "reminder_upcoming",
        "reminder_due",
      ],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "leave_approved",
    label: "Leave approved",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 60,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: ["leave_approval_required"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "leave_rejected",
    label: "Leave rejected",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 70,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: ["leave_approval_required"],
      clearOlderSameEvent: true,
    },
  }),

  event({
    id: "new_note",
    label: "New note added",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 10,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "date_change",
    label: "Date changed",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 20,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "new_upload",
    label: "New file uploaded",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 30,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "task_status_change",
    label: "Task status change",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 40,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "task_closed",
    label: "Task closed",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 50,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "status_change",
    label: "Item status changed",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 60,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "item_closed",
    label: "Item closed",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 70,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "urgency_change",
    label: "Urgency changed",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 80,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "recipient_away",
    label: "Person Away",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 100,
    classification: NOTIFICATION_CLASSIFICATIONS.SYSTEM,
    recipientStrategy: system,
    targetType: info,
    emailAction: noEmailCta,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "ordering_item_ordered",
    label: "Ordering item ordered",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 110,
    targetType: board,
    targetKey: "ordering_board",
    rendererKey: "ordering_item_status",
    emailRendererKey: "ordering_item_status",
    visibilityPermissions: ["ordering"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openOrderingEmailAction,
  }),
  event({
    id: "ordering_item_delivered",
    label: "Ordering item delivered",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 120,
    targetType: board,
    targetKey: "ordering_board",
    rendererKey: "ordering_item_status",
    emailRendererKey: "ordering_item_status",
    visibilityPermissions: ["ordering"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openOrderingEmailAction,
  }),
  event({
    id: "ordering_item_cancelled",
    label: "Ordering item cancelled",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 130,
    targetType: board,
    targetKey: "ordering_board",
    rendererKey: "ordering_item_status",
    emailRendererKey: "ordering_item_status",
    visibilityPermissions: ["ordering"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openOrderingEmailAction,
  }),
  event({
    id: "ordering_item_reopened",
    label: "Ordering item reopened",
    groupId: NOTIFICATION_GROUPS.ACTIVITY,
    order: 140,
    targetType: board,
    targetKey: "ordering_board",
    rendererKey: "ordering_item_status",
    emailRendererKey: "ordering_item_status",
    visibilityPermissions: ["ordering"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openOrderingEmailAction,
  }),
  event({
    id: "leave_created_for_you",
    label: "Leave created for you",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 80,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "leave_updated_for_you",
    label: "Leave updated for you",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 90,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "leave_cancelled_for_you",
    label: "Leave cancelled for you",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 100,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),

  event({
    id: "task_due_today",
    label: "Task due today",
    groupId: NOTIFICATION_GROUPS.SCHEDULE,
    order: 10,
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: ["task_due_reminder"],
    },
  }),
  event({
    id: "task_due_reminder",
    label: "Task due soon",
    groupId: NOTIFICATION_GROUPS.SCHEDULE,
    order: 20,
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "project_due_today",
    label: "Project due today",
    groupId: NOTIFICATION_GROUPS.SCHEDULE,
    order: 30,
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: ["project_due_reminder"],
    },
  }),
  event({
    id: "project_due_reminder",
    label: "Project due soon",
    groupId: NOTIFICATION_GROUPS.SCHEDULE,
    order: 40,
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "patient_appt_reminder",
    label: "Patient appointment",
    groupId: NOTIFICATION_GROUPS.SCHEDULE,
    order: 50,
    visibilityPermissions: ["patients"],
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "maintenance_due_reminder",
    label: "Maintenance due soon",
    groupId: NOTIFICATION_GROUPS.SCHEDULE,
    order: 60,
    visibilityPermissions: ["maintenance"],
    defaultControl: timedControl,
    defaultSetting: reminderDefault,
    emailAction: viewMaintenanceStreamEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "event_upcoming",
    label: "Event due soon",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 110,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "event_today",
    label: "Event today",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 120,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: ["event_upcoming"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "event_due",
    label: "Event now",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 130,
    targetType: board,
    targetKey: "calendar_board",
    rendererKey: "event_today",
    emailRendererKey: "event_today",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: ["event_upcoming"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "reminder_upcoming",
    label: "Reminder due soon",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 140,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "reminder_due",
    label: "Reminder now",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 150,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: ["reminder_upcoming"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "leave_upcoming",
    label: "Leave starting soon",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 160,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "leave_today",
    label: "Leave today",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 170,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: ["leave_upcoming"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "labs_due_reminder",
    label: "Lab reminder",
    groupId: NOTIFICATION_GROUPS.SCHEDULE,
    order: 75,
    visibilityPermissions: ["labs"],
    defaultControl: timedControl,
    defaultSetting: reminderDefault,
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),

  event({
    id: "task_overdue",
    label: "Task overdue",
    groupId: NOTIFICATION_GROUPS.INACTIVITY,
    order: 10,
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: ["task_due_today"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "task_inactive",
    label: "Task inactive",
    groupId: NOTIFICATION_GROUPS.INACTIVITY,
    order: 20,
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "project_overdue",
    label: "Project overdue",
    groupId: NOTIFICATION_GROUPS.INACTIVITY,
    order: 30,
    visibilityPermissions: ["projects"],
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: ["project_due_today"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "project_inactive",
    label: "Project inactive",
    groupId: NOTIFICATION_GROUPS.INACTIVITY,
    order: 40,
    visibilityPermissions: ["projects"],
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "patient_inactive",
    label: "Patient inactive",
    groupId: NOTIFICATION_GROUPS.INACTIVITY,
    order: 50,
    visibilityPermissions: ["patients"],
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "asset_task_inactive",
    label: "Asset task inactive",
    groupId: NOTIFICATION_GROUPS.INACTIVITY,
    order: 60,
    visibilityPermissions: ["assets"],
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "maintenance_overdue",
    label: "Maintenance overdue",
    groupId: NOTIFICATION_GROUPS.INACTIVITY,
    order: 70,
    visibilityPermissions: ["maintenance"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: viewMaintenanceStreamEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: ["maintenance_due_reminder"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "labs_overdue",
    label: "Lab overdue",
    groupId: NOTIFICATION_GROUPS.INACTIVITY,
    order: 75,
    visibilityPermissions: ["labs"],
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: ["labs_due_reminder"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "maintenance_inactive",
    label: "Maintenance inactive",
    groupId: NOTIFICATION_GROUPS.INACTIVITY,
    order: 80,
    visibilityPermissions: ["maintenance"],
    defaultControl: timedControl,
    defaultSetting: inactivityDefault,
    emailAction: viewMaintenanceStreamEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "compliance_due_soon",
    label: "Compliance requirement due soon",
    groupId: NOTIFICATION_GROUPS.SCHEDULE,
    order: 85,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    targetType: board,
    targetKey: "compliance_board",
    rendererKey: "compliance_due",
    emailRendererKey: "compliance_due",
    settingsSurface: NOTIFICATION_SETTINGS_SURFACES.STANDARD,
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: noEmailCta,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: ["compliance_due_now", "compliance_overdue"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "compliance_due_now",
    label: "Compliance requirement due now",
    groupId: NOTIFICATION_GROUPS.SCHEDULE,
    order: 86,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    targetType: board,
    targetKey: "compliance_board",
    rendererKey: "compliance_due",
    emailRendererKey: "compliance_due",
    settingsSurface: NOTIFICATION_SETTINGS_SURFACES.STANDARD,
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: noEmailCta,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: ["compliance_due_soon", "compliance_overdue"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "compliance_overdue",
    label: "Compliance requirement overdue",
    groupId: NOTIFICATION_GROUPS.INACTIVITY,
    order: 76,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    targetType: board,
    targetKey: "compliance_board",
    rendererKey: "compliance_due",
    emailRendererKey: "compliance_due",
    settingsSurface: NOTIFICATION_SETTINGS_SURFACES.STANDARD,
    defaultControl: directControl,
    defaultSetting: inAppDefault,
    emailAction: noEmailCta,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: ["compliance_due_soon", "compliance_due_now"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "labs_inactive",
    label: "Lab inactive",
    groupId: NOTIFICATION_GROUPS.INACTIVITY,
    order: 90,
    visibilityPermissions: ["labs"],
    defaultControl: timedControl,
    defaultSetting: inactivityDefault,
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),

  event({
    id: "team_asset_service",
    label: "Team: Asset Service Due",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 10,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    visibilityPermissions: ["assets"],
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "team_asset_service_overdue",
    label: "Team: Asset Service Overdue",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 20,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    visibilityPermissions: ["assets"],
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: ["team_asset_service"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "team_patient_high_priority",
    label: "Team: High Urgency Patient",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 50,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    visibilityPermissions: ["patients"],
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "team_patient_inactive",
    label: "Team: Inactive Patient (High Urgency)",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 60,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    visibilityPermissions: ["patients"],
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "team_labs_high_priority",
    label: "Team: High Urgency Lab",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 65,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    visibilityPermissions: ["labs"],
    defaultTeamSetting: teamPriorityDefault,
    emailAction: viewContextEmailAction,
  }),
  event({
    id: "team_labs_inactive",
    label: "Team: Inactive Lab",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 67,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    visibilityPermissions: ["labs"],
    defaultTeamSetting: teamTimedPriorityDefault,
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "team_task_stalled",
    label: "Team: Stalled Task (High Urgency)",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 70,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    emailAction: viewContextEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "team_maintenance_due",
    label: "Team: Maintenance Due",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 80,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    visibilityPermissions: ["maintenance"],
    defaultTeamSetting: teamTimedDefault,
    emailAction: viewMaintenanceStreamEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "team_maintenance_high_priority",
    label: "Team: High Urgency Maintenance",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 90,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    visibilityPermissions: ["maintenance"],
    defaultTeamSetting: teamPriorityDefault,
    emailAction: viewMaintenanceStreamEmailAction,
  }),
  event({
    id: "team_maintenance_inactive",
    label: "Team: Inactive Maintenance",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 100,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    visibilityPermissions: ["maintenance"],
    defaultTeamSetting: teamTimedPriorityDefault,
    emailAction: viewMaintenanceStreamEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "team_recall_draft_inactive",
    label: "Team: Inactive Recall Draft",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 110,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    targetType: board,
    targetKey: "recalls_board",
    visibilityPermissions: ["recalls"],
    defaultTeamSetting: teamTimedDefault,
    emailAction: reviewRecallsEmailAction,
    cleanupPolicy: {
      identityField: "dedupeKey",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "team_recall_drafts_high_count",
    label: "Team: High Recall Draft Count",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 112,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    targetType: board,
    targetKey: "recalls_board",
    visibilityPermissions: ["recalls"],
    defaultTeamSetting: teamCountDefault,
    emailAction: reviewRecallsEmailAction,
    cleanupPolicy: {
      identityField: "dedupeKey",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "team_ordering_item_added",
    label: "Team: Ordering item added",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 115,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    targetType: board,
    targetKey: "ordering_board",
    rendererKey: "ordering_item_added",
    emailRendererKey: "ordering_item_added",
    visibilityPermissions: ["ordering"],
    defaultTeamSetting: Object.freeze({
      teamId: null,
      inApp: true,
      email: false,
    }),
    emailAction: openOrderingEmailAction,
    cleanupPolicy: {
      identityField: "dedupeKey",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "team_leave_today",
    label: "Team: Leave today",
    groupId: NOTIFICATION_GROUPS.CALENDAR_LEAVE,
    order: 180,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    targetType: board,
    targetKey: "calendar_board",
    visibilityPermissions: ["calendar"],
    defaultTeamSetting: teamInAppDefault,
    emailAction: openCalendarEmailAction,
    cleanupPolicy: {
      identityField: "contextId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "team_maintenance_issue_new",
    label: "Team: New Maintenance Issue",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 120,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    visibilityPermissions: ["maintenance"],
    defaultTeamSetting: teamInAppDefault,
    emailAction: viewMaintenanceStreamEmailAction,
  }),
  event({
    id: "team_maintenance_issue_closed",
    label: "Team: Maintenance Issue Closed",
    groupId: NOTIFICATION_GROUPS.OPERATIONS,
    order: 130,
    classification: NOTIFICATION_CLASSIFICATIONS.TEAM,
    recipientStrategy: team,
    visibilityPermissions: ["maintenance"],
    defaultTeamSetting: teamInAppDefault,
    emailAction: viewMaintenanceStreamEmailAction,
  }),

  event({
    id: "drug_approve_use",
    label: "Approval: Drug Usage",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 80,
    classification: NOTIFICATION_CLASSIFICATIONS.APPROVAL,
    recipientStrategy: approval,
    targetType: modal,
    targetKey: "approval",
    visibilityPermissions: ["drugs"],
    emailAction: reviewRequestEmailAction,
  }),
  event({
    id: "drug_approve_move",
    label: "Approval: Drug Relocation",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 90,
    classification: NOTIFICATION_CLASSIFICATIONS.APPROVAL,
    recipientStrategy: approval,
    targetType: modal,
    targetKey: "approval",
    visibilityPermissions: ["drugs"],
    emailAction: reviewRequestEmailAction,
  }),
  event({
    id: "drug_approve_restock",
    label: "Approval: Drug Restock",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 100,
    classification: NOTIFICATION_CLASSIFICATIONS.APPROVAL,
    recipientStrategy: approval,
    targetType: modal,
    targetKey: "approval",
    visibilityPermissions: ["drugs"],
    emailAction: reviewRequestEmailAction,
  }),
  event({
    id: "drug_approve_correction",
    label: "Approval: Drug Correction",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 110,
    classification: NOTIFICATION_CLASSIFICATIONS.APPROVAL,
    recipientStrategy: approval,
    targetType: modal,
    targetKey: "approval",
    visibilityPermissions: ["drugs"],
    emailAction: reviewRequestEmailAction,
  }),
  event({
    id: "drug_approve_loss",
    label: "Approval: Drug Loss",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 120,
    classification: NOTIFICATION_CLASSIFICATIONS.APPROVAL,
    recipientStrategy: approval,
    targetType: modal,
    targetKey: "approval",
    visibilityPermissions: ["drugs"],
    emailAction: reviewRequestEmailAction,
  }),
  event({
    id: "drug_approve_void",
    label: "Approval: Drug Void",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 130,
    classification: NOTIFICATION_CLASSIFICATIONS.APPROVAL,
    recipientStrategy: approval,
    targetType: modal,
    targetKey: "approval",
    visibilityPermissions: ["drugs"],
    emailAction: reviewRequestEmailAction,
  }),
  event({
    id: "drug_approve_audit_start",
    label: "Approval: Start Audit",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 140,
    classification: NOTIFICATION_CLASSIFICATIONS.APPROVAL,
    recipientStrategy: approval,
    targetType: modal,
    targetKey: "approval",
    visibilityPermissions: ["drugs"],
    emailAction: reviewRequestEmailAction,
  }),
  event({
    id: "drug_approve_audit_cancel",
    label: "Approval: Cancel Audit",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 150,
    classification: NOTIFICATION_CLASSIFICATIONS.APPROVAL,
    recipientStrategy: approval,
    targetType: modal,
    targetKey: "approval",
    visibilityPermissions: ["drugs"],
    emailAction: reviewRequestEmailAction,
  }),
  event({
    id: "drug_approve_audit_finalize",
    label: "Approval: Finalise Audit",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 160,
    classification: NOTIFICATION_CLASSIFICATIONS.APPROVAL,
    recipientStrategy: approval,
    targetType: modal,
    targetKey: "approval",
    visibilityPermissions: ["drugs"],
    emailAction: reviewRequestEmailAction,
  }),
  event({
    id: "drug_approval_approved",
    label: "Drug Transaction Approved",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 190,
    targetType: board,
    targetKey: "drug_history",
    recipientStrategy: direct,
    visibilityPermissions: ["drugs"],
    emailAction: noEmailCta,
  }),
  event({
    id: "drug_approval_rejected",
    label: "Drug Transaction Rejected",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 210,
    targetType: board,
    targetKey: "drug_history",
    recipientStrategy: direct,
    visibilityPermissions: ["drugs"],
    emailAction: resubmitTransactionEmailAction,
  }),
  event({
    id: "drug_transaction_voided",
    label: "Drug Transaction Voided",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 220,
    targetType: board,
    targetKey: "drug_history",
    recipientStrategy: direct,
    visibilityPermissions: ["drugs"],
    emailAction: resubmitTransactionEmailAction,
  }),
  event({
    id: "drug_low_stock_warning",
    label: "Critical Low Stock Warning",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 230,
    classification: NOTIFICATION_CLASSIFICATIONS.SYSTEM,
    targetType: board,
    targetKey: "drug_stock",
    recipientStrategy: role,
    visibilityPermissions: ["drugs"],
    emailAction: reviewStockEmailAction,
    cleanupPolicy: {
      identityField: "dedupeKey",
      supersedesEvents: ["drug_low_stock_reminder"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "drug_low_stock_reminder",
    label: "Low Stock Reminder",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 240,
    classification: NOTIFICATION_CLASSIFICATIONS.SYSTEM,
    targetType: board,
    targetKey: "drug_stock",
    recipientStrategy: role,
    visibilityPermissions: ["drugs"],
    emailAction: reviewStockEmailAction,
    cleanupPolicy: {
      identityField: "dedupeKey",
      supersedesEvents: ["drug_low_stock_warning"],
      clearOlderSameEvent: true,
    },
  }),
  event({
    id: "drug_negative_stock",
    label: "Negative Stock Alert",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 250,
    classification: NOTIFICATION_CLASSIFICATIONS.SYSTEM,
    targetType: board,
    targetKey: "drug_stock",
    recipientStrategy: role,
    visibilityPermissions: ["drugs"],
    emailAction: reviewStockEmailAction,
  }),
  event({
    id: "drug_audit_discrepancy",
    label: "Audit Discrepancy",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 260,
    classification: NOTIFICATION_CLASSIFICATIONS.SYSTEM,
    targetType: board,
    targetKey: "drug_audit",
    recipientStrategy: role,
    visibilityPermissions: ["drugs"],
    emailAction: reviewAuditEmailAction,
  }),
  event({
    id: "drug_audit_overdue",
    label: "Audit Overdue",
    groupId: NOTIFICATION_GROUPS.DRUGS,
    order: 270,
    classification: NOTIFICATION_CLASSIFICATIONS.SYSTEM,
    targetType: board,
    targetKey: "drug_audit",
    recipientStrategy: role,
    visibilityPermissions: ["drugs"],
    emailAction: auditDrugsEmailAction,
    cleanupPolicy: {
      identityField: "parentId",
      supersedesEvents: [],
      clearOlderSameEvent: true,
    },
  }),
]);

const EVENT_DEFINITIONS_BY_ID = Object.freeze(
    EVENT_DEFINITIONS.reduce((acc, definition) => {
      acc[definition.id] = definition;
      return acc;
    }, {}),
);

function getNotificationEventDefinition(eventId) {
  return EVENT_DEFINITIONS_BY_ID[eventId] || null;
}

function getNotificationEventLabel(eventId) {
  const definition = getNotificationEventDefinition(eventId);
  if (definition) return definition.label;

  return String(eventId || "")
      .split("_")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
}

function isRetiredNotificationEvent(eventId) {
  return RETIRED_NOTIFICATION_EVENT_IDS.includes(eventId);
}

function getRequiredNotificationConfigEntries() {
  return EVENT_DEFINITIONS.reduce((acc, definition) => {
    if (!definition.defaultControl && !definition.defaultSetting) {
      return acc;
    }

    acc[definition.id] = {
      control: definition.defaultControl ?
        {...definition.defaultControl} :
        null,
      default: definition.defaultSetting ?
        {...definition.defaultSetting} :
        null,
    };
    return acc;
  }, {});
}

function getRequiredTeamNotificationConfigEntries() {
  return EVENT_DEFINITIONS.reduce((acc, definition) => {
    if (!definition.defaultTeamSetting) {
      return acc;
    }

    acc[definition.id] = {...definition.defaultTeamSetting};
    return acc;
  }, {});
}

function getNotificationCleanupPolicy(eventId) {
  const definition = getNotificationEventDefinition(eventId);
  return definition?.cleanupPolicy || null;
}

function getNotificationRouteTarget(targetKey) {
  return NOTIFICATION_ROUTE_TARGETS[targetKey] || null;
}

module.exports = {
  EVENT_DEFINITIONS,
  EVENT_DEFINITIONS_BY_ID,
  GROUP_DEFINITIONS,
  NOTIFICATION_CLASSIFICATIONS,
  NOTIFICATION_EMAIL_CTA_TYPES,
  NOTIFICATION_GROUPS,
  NOTIFICATION_ROUTE_TARGETS,
  NOTIFICATION_SETTINGS_SURFACES,
  NOTIFICATION_TARGET_TYPES,
  RECIPIENT_STRATEGIES,
  RETIRED_NOTIFICATION_EVENT_IDS,
  getNotificationCleanupPolicy,
  getNotificationEventDefinition,
  getNotificationEventLabel,
  getNotificationRouteTarget,
  getRequiredNotificationConfigEntries,
  getRequiredTeamNotificationConfigEntries,
  isRetiredNotificationEvent,
};
